import React, { useEffect, useCallback } from 'react'
import styled from 'styled-components'
import { Modal, Spin } from 'antd'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { PATH as PATH_AUTH } from 'src/pages/auth'
import { PATH as AUTOMATIC_PLAN_PATH } from 'src/pages/automaticPlan'
import { useAppDispatch, useAppSelector } from 'src/store'
import { checkExistsUserTreatment } from 'src/useCase/userTreatment'
import LocalStorage from 'src/utils/LocalStorage'
import useCreateTreatment from 'src/hook/useCreateTreatment'
import { getClinicDetail } from 'src/useCase/clinic'
import { getInfo } from 'src/useCase/user'
import useHandleTreatmentRedirect from 'src/hook/useHandleTreatmentRedirect'

interface CheckUserExistsModalProps {
  isOpen: boolean
  onClose?: () => void
}

const CheckUserExistsModal: React.FC<CheckUserExistsModalProps> = ({
  isOpen,
}) => {
  const accessToken = LocalStorage.accessToken
  const user = useAppSelector((state) => state.ui.app.authInfo)
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const menuId = searchParams.get('menuId')
  const planId = searchParams.get('planId')
  const uuid = searchParams.get('uuid')
  const clinicId = searchParams.get('clinicId')
  const from = searchParams.get('from') || 'LP'
  const { createTreatment } = useCreateTreatment()
  const { handleRedirect } = useHandleTreatmentRedirect()
  const userClinicUuid = user?.clinics?.[0]?.uuid
  const userClinicId = user?.clinics?.[0]?.id
  const isDifferentClinic = Boolean(
    uuid && userClinicUuid && uuid !== userClinicUuid,
  )
  const currentUuid = userClinicUuid || uuid
  const currentClinicId = userClinicId || clinicId

  const buildQuery = (extra: Record<string, string | undefined> = {}) => {
    const params = new URLSearchParams()

    if (currentUuid) params.set('uuid', currentUuid)
    if (currentClinicId) params.set('clinicId', currentClinicId)
    if (from) params.set('from', from)
    if (!isDifferentClinic) {
      if (menuId) params.set('menuId', menuId)
      if (planId) params.set('planId', planId)
    }
    Object.entries(extra).forEach(([key, value]) => {
      if (!value) {
        params.delete(key)
      } else {
        params.set(key, value)
      }
    })

    return params.toString()
  }

  const handleCheckUser = useCallback(
    async (isTelemedicine: boolean, currentUser: any) => {
      if (!accessToken) {
        if (isTelemedicine) {
          LocalStorage.setTelemedicine('true')
          const redirectPath = planId
            ? PATH_AUTH.REGISTER
            : PATH_AUTH.MEDICAL_SPECIALTY

          return navigate(`${redirectPath}?${buildQuery()}`, { replace: true })
        }

        const redirectPath = planId
          ? PATH_AUTH.PRECAUTION_MODAL_PLAN
          : PATH_AUTH.MEDICAL_SPECIALTY
        return navigate(`${redirectPath}?${buildQuery()}`, { replace: true })
      }

      if (accessToken && menuId && planId) {
        try {
          const action = await dispatch(
            checkExistsUserTreatment({ menu_id: menuId }),
          )

          if (
            checkExistsUserTreatment.fulfilled.match(action) &&
            !isDifferentClinic
          ) {
            if (isTelemedicine) {
              const newUserTreatment = await createTreatment(
                Number(menuId),
                planId,
                { menu_id: Number(menuId), plan_id: planId },
              )

              if (!currentUser?.otp_verified_at && currentUser?.phone) {
                return navigate(
                  `${AUTOMATIC_PLAN_PATH.SMS_AUTH}?${buildQuery()}`,
                )
              }

              if (newUserTreatment) {
                return handleRedirect(newUserTreatment, buildQuery())
              }
            }

            return navigate(
              `${PATH_AUTH.PRECAUTION_MODAL_PLAN}?${buildQuery()}`,
              {
                replace: true,
              },
            )
          }

          if (checkExistsUserTreatment.rejected.match(action)) {
            return navigate(
              `${PATH_AUTH.MEDICAL_SPECIALTY}?${buildQuery({
                menuId: undefined,
                planId: undefined,
                exists: 'true',
              })}`,
              { replace: true },
            )
          }
        } catch (error) {
          return navigate(`${PATH_AUTH.MEDICAL_SPECIALTY}?${buildQuery()}`, {
            replace: true,
          })
        }
      }

      return navigate(`${PATH_AUTH.MEDICAL_SPECIALTY}?${buildQuery()}`, {
        replace: true,
      })
    },
    [
      accessToken,
      menuId,
      planId,
      searchParams,
      dispatch,
      navigate,
      isDifferentClinic,
    ],
  )

  useEffect(() => {
    let isMounted = true

    const ensureUserAndCheck = async () => {
      if (!isMounted) return

      let currentUser = user

      if (accessToken && !user) {
        try {
          currentUser = await dispatch(
            getInfo({ with: 'reservations,userTreatments' }),
          ).unwrap()
        } catch {
          currentUser = null
        }
      }

      const userClinicUuid = currentUser?.clinics?.[0]?.uuid
      const currentUuid = userClinicUuid || uuid
      const isDifferentClinic = Boolean(
        uuid && userClinicUuid && uuid !== userClinicUuid,
      )

      if (isDifferentClinic) LocalStorage.setDifferentClinicFlag(true)

      let isTelemedicine = false
      if (currentUuid) {
        try {
          const result = await dispatch(
            getClinicDetail({ uuid: currentUuid }),
          ).unwrap()
          isTelemedicine = Boolean(result?.telemedicine_enabled)
        } catch {
          isTelemedicine = false
        }
      }

      if (isMounted) {
        await handleCheckUser(isTelemedicine, currentUser)
      }
    }

    ensureUserAndCheck()

    return () => {
      isMounted = false
    }
  }, [accessToken, user, uuid, clinicId, dispatch, handleCheckUser])

  return (
    <Modal
      open={isOpen}
      centered
      closable={false}
      footer={null}
      maskClosable={false}
      className="check-user-exists-modal"
    >
      <Container>
        <Spin size="large" />
        <div className="mt-3">患者情報を処理しています。</div>
      </Container>
    </Modal>
  )
}

const Container = styled.div`
  text-align: center;
  padding: 16px;

  .ant-spin-dot-item {
    background-color: #292e98;
  }
}
`

export default CheckUserExistsModal
